import { useEffect, useState } from 'react'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  useIsOrganisationInactive,
  useIsStatusCheckSignUp,
  usePostHogFeatureFlag
} from '@percent/cause-dashboard/common/hooks'
import { useTranslation } from 'react-i18next'
import styles from './NotificationBar.module.scss'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { selectAuthState } from '@percent/cause-dashboard/context/auth'
import { VerificationNotificationText } from './NotificationBarText'
import { ReactComponent as CloseIcon } from '@percent/cause-dashboard/common/assets/images/close-icon-white.svg'
import { useNotificationBar } from '@percent/cause-dashboard/common/hooks/useNotificationBar/useNotificationBar'
import { NotificationBarProps } from './NotificationBar.types'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { useQuery } from '../../hooks/useQuery/useQuery'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Loader } from '@percent/cause-dashboard/common/components'
import { addHours, isAfter } from 'date-fns'
import { useSitewideMessage } from '../../hooks/useSitewideMessage/useSitewideMessage'
import { Button } from '@percent/lemonade'
import { useNavigate, useLocation } from 'react-router-dom'
import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { PopUpModalContainer as ExplicitSmartApply } from '../explicitSmartApply/PopUpModalContainer'
import { applications } from '@percent/cause-dashboard/app/myDiscounts/data/applications'
import { IntroModals } from '../introModals'

enum NotificationType {
  CLAIM_PENDING,
  CLAIM_REJECTED,
  APPLICATION_STATUS,
  BANK_REJECTED,
  DISMISSABLE_ANOUNCEMENT,
  NONE
}

const CANVA_PARTNER_ID = 'partner_000000CMTqjAZ1lf21cvt6GyJnUI4'

export const dissmissedAnouncementKey = 'dissmissedAnouncement'

const isApplicationValidation = (configurationId: string | undefined, partnerId: string | undefined) =>
  Object.values(applications)
    .flat()
    .some(application => application.configurationId === configurationId && application.partnerId === partnerId)

export function NotificationBar({ config }: NotificationBarProps) {
  const {
    authState: appState,
    authState: { claimOrganisation, user }
  } = useAuthState()
  const isSmartApplyBlocked = useIsOrganisationInactive()
  const isNonprofit = claimOrganisation?.type === 'nonprofit'
  const { storeService } = useServices()
  const navigate = useNavigate()
  const location = useLocation()
  const [{ data: validationSubmissionsData, isLoading }] = useQuery(storeService.getValidationSubmissions, {
    accountId: user?.id,
    organisationId: claimOrganisation?.id
  })
  const { rejectedBankDetails } = selectAuthState(appState)
  const { t } = useTranslation()
  const [notificationType, setNotificationType] = useState(NotificationType.NONE)
  const sitewideMessage = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.TIL_CAUSES_PORTAL_SITEWIDE_MESSAGE)
  const isPartnerApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_PARTNER_APPLY_ENABLED)
  const { setIsOpened } = useNotificationBar()
  const [isApplicationNotification, setIsApplicationNotification] = useState(false)
  const { shouldShowSitewideMessage } = useSitewideMessage()
  const isStatusCheckSignUp = useIsStatusCheckSignUp()

  const isRecentlyProcessed = (date: string): boolean => {
    const processedAtDate = new Date(date)
    const threshold = addHours(new Date(), -24)
    return isAfter(processedAtDate, threshold)
  }

  const hasCanvaApplication = validationSubmissionsData?.data?.some(vs => vs.partnerId === CANVA_PARTNER_ID) ?? true // fallback to true to be safe
  const validationSubmission = validationSubmissionsData?.data?.[0]

  useEffect(() => {
    if (isApplicationValidation(validationSubmission?.configurationId, validationSubmission?.partnerId)) {
      const isPending = validationSubmission?.status === 'PENDING'
      const isProcessedRecently =
        validationSubmission?.processedAt && isRecentlyProcessed(validationSubmission?.processedAt)

      if (isPending || isProcessedRecently) {
        setIsApplicationNotification(true)
      }
    }
  }, [validationSubmission])

  useEffect(() => {
    const checkNotificationType = () => {
      const dissmissedCurrentAnouncement =
        localStorage.getItem(dissmissedAnouncementKey) === config.strings.notificationBanner.textKey
      let type = NotificationType.NONE
      if (isApplicationNotification) {
        type = NotificationType.APPLICATION_STATUS
      } else if (!isStatusCheckSignUp && claimOrganisation?.rejectedAt) {
        type = NotificationType.CLAIM_REJECTED
      } else if (!isStatusCheckSignUp && !claimOrganisation?.acceptedAt) {
        type = NotificationType.CLAIM_PENDING
      } else if (rejectedBankDetails) {
        type = NotificationType.BANK_REJECTED
      } else if (!dissmissedCurrentAnouncement && sitewideMessage) {
        type = NotificationType.DISMISSABLE_ANOUNCEMENT
      }
      setNotificationType(type)
      setIsOpened(type !== NotificationType.NONE && !shouldShowSitewideMessage)
    }
    checkNotificationType()
  }, [
    claimOrganisation,
    rejectedBankDetails,
    sitewideMessage,
    isApplicationNotification,
    isStatusCheckSignUp,
    setNotificationType,
    setIsOpened,
    config,
    shouldShowSitewideMessage
  ])

  const getNotificationText = (): LocaleKey => {
    switch (notificationType) {
      case NotificationType.APPLICATION_STATUS:
        return 'modalPartnerApplication.banner.text'
      case NotificationType.CLAIM_PENDING:
        return 'typography.verificationNotification.pending'
      case NotificationType.CLAIM_REJECTED:
        return 'typography.verificationNotification.rejected'
      case NotificationType.BANK_REJECTED:
        return 'typography.rejectedBankDetailsNotification'
      default:
        return config.strings.notificationBanner.textKey as LocaleKey
    }
  }

  const getButtonText = (): LocaleKey => {
    switch (notificationType) {
      case NotificationType.APPLICATION_STATUS:
        return 'modalPartnerApplication.CTA.goToMyDiscounts'
      case NotificationType.CLAIM_PENDING:
      case NotificationType.CLAIM_REJECTED:
        return 'typography.verificationNotification.contactUs'
      case NotificationType.BANK_REJECTED:
        return 'typography.reachOutToUsLink'
      default:
        return config.strings.notificationBanner.buttonKey as LocaleKey
    }
  }

  const handleApplicationNotificationButtonClick = () => {
    navigate(RoutePath.MY_DISCOUNTS)
  }

  const getButtonHref = () =>
    [NotificationType.CLAIM_PENDING, NotificationType.CLAIM_REJECTED, NotificationType.BANK_REJECTED].includes(
      notificationType
    )
      ? HELP_LINK
      : config.urls.notificationBannerLink

  const handleButtonClick = () => {
    if (notificationType === NotificationType.APPLICATION_STATUS) {
      handleApplicationNotificationButtonClick()
    } else {
      getButtonHref()
    }
  }

  const dissmissNotification = () => {
    if (config.strings.notificationBanner.textKey) {
      localStorage.setItem(dissmissedAnouncementKey, config.strings.notificationBanner.textKey)
    }
    setNotificationType(NotificationType.NONE)
    setIsOpened(false)
  }

  const SmartApplyModal = <ExplicitSmartApply hasCanvaApplication={hasCanvaApplication} />

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      {notificationType !== NotificationType.NONE && !shouldShowSitewideMessage && (
        <div
          className={
            notificationType === NotificationType.DISMISSABLE_ANOUNCEMENT
              ? styles.generalNotification
              : styles.verificationNotification
          }
        >
          <VerificationNotificationText
            notificationText={t(getNotificationText())}
            buttonText={t(getButtonText())}
            buttonUrl={getButtonHref() ?? ''}
            key={notificationType}
            onButtonClick={handleButtonClick}
            showApplicationStatus={notificationType === NotificationType.APPLICATION_STATUS}
          />
          {notificationType === NotificationType.DISMISSABLE_ANOUNCEMENT && (
            <div className={styles.closeNotificationButton}>
              <Button onClick={dissmissNotification}>
                <CloseIcon />
              </Button>
            </div>
          )}
        </div>
      )}
      {location.pathname === '/product-offers' &&
        isPartnerApplyEnabled &&
        !isSmartApplyBlocked &&
        isNonprofit &&
        SmartApplyModal}
      {location.pathname === '/home' && (
        <IntroModals
          validationData={validationSubmission}
          hasCanvaApplication={hasCanvaApplication}
          isSmartApplyBlocked={isSmartApplyBlocked}
          isNonprofit={isNonprofit}
        />
      )}
    </>
  )
}
